<template>
    <div class="bex-card" :class="{ 'bex-card--open': expand }">
        <div class="bex-row" @click="toggleExpand">
            <button-tooltip v-if="showArrows" :tooltip="$t('common.previous')" top>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small v-on="on" v-bind="attrs" class="bex-arrow"
                        @click.stop="$emit('click:prev')">
                        <v-icon size="18">mdi-chevron-left</v-icon>
                    </v-btn>
                </template>
            </button-tooltip>

            <v-icon v-if="!readonly && !circuitType" class="handle-item bex-drag" size="16" color="txtMuted"
                @click.stop @contextmenu.prevent="() => { return false }">mdi-drag-vertical</v-icon>

            <exercise-img-video class="bex-thumb" :is-second-dialog="isSecondDialog" :video="exerciseVideo"
                :img="exerciseImg" :name="exercise.name" :isYoutubeVideo="isYoutubeVideo" @click.native.stop />

            <div class="bex-info">
                <div class="bex-name text-truncate">{{ exercise.name }}</div>
                <div v-if="!noConfigYet" class="bex-sets">
                    {{ exerciseSets }} {{ exercise.eachSide ? `| ${$t('exercises.each_side')}` : '' }}
                </div>
                <div v-else class="bex-sets bex-sets--error">
                    {{ $t('exercises.no_config_exercise_yet') }}
                    <v-icon color="error" size="14">mdi-gesture-tap</v-icon>
                </div>
            </div>

            <v-menu v-if="!readonly" offset-y>
                <template v-slot:activator="{ on }">
                    <button type="button" class="bex-menu-btn" v-on="on" @click.stop>
                        <v-icon size="16">mdi-dots-vertical</v-icon>
                    </button>
                </template>
                <v-list dense>
                    <v-list-item link v-if="!calendarMode" @click.prevent="onClickChange">
                        <v-icon size="18" color="txtSecondary" class="mr-2">mdi-swap-horizontal</v-icon>
                        <v-list-item-title>{{ $t('common.change') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item link @click.prevent="onClickDuplicate">
                        <v-icon size="18" color="txtSecondary" class="mr-2">mdi-content-copy</v-icon>
                        <v-list-item-title>{{ $t('common.duplicate') }}</v-list-item-title>
                    </v-list-item>
                    <v-divider />
                    <v-list-item link @click.prevent="onClickDelete">
                        <v-icon size="18" color="error" class="mr-2">mdi-trash-can-outline</v-icon>
                        <v-list-item-title class="error--text">{{ $t('common.delete') }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>

            <button-tooltip v-if="showArrows" :tooltip="$t('common.next')" top>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small v-on="on" v-bind="attrs" class="bex-arrow"
                        @click.stop="$emit('click:next')">
                        <v-icon size="18">mdi-chevron-right</v-icon>
                    </v-btn>
                </template>
            </button-tooltip>

            <span v-if="!readonly" class="bex-chevron" :class="{ open: expand }">
                <v-icon size="16" color="txtMuted">mdi-chevron-down</v-icon>
            </span>
        </div>

        <div v-show="expand" class="bex-body">
            <exercise-series-editor :exercise="exercise" :category-exercise="categoryExercise"
                :circuit-type="circuitType" @update="$emit('update', $event)" @click:rest="$emit('click:rest')" />
        </div>
    </div>
</template>

<script>
import { randomString } from "@/utils/Common";
import ExerciseSeriesEditor from "@/components/coach/routines/ExerciseSeriesEditor";
import ExerciseImgVideo from "@/components/common/routines/ExerciseImgVideo";
import ButtonTooltip from "@/components/common/ButtonTooltip";

export default {
    name: "BlockExerciseCard",
    components: { ExerciseImgVideo, ExerciseSeriesEditor, ButtonTooltip },
    props: {
        exercise: {
            type: Object,
            required: true
        },
        readonly: {
            type: Boolean,
            default: false
        },
        isSecondDialog: {
            type: Boolean,
            default: false
        },
        block: {
            type: Object,
            default: null
        },
        calendarMode: {
            type: Boolean,
            default: false
        },
        showArrows: {
            type: Boolean,
            default: false
        },
        circuitType: {
            type: String,
            default: null,
        },
        createBlockMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            expand: false,
            dataSet: null,
        }
    },
    methods: {
        toggleExpand() {
            const vm = this
            if (vm.readonly || vm.calendarMode) {
                return vm.$emit('click:exercise', vm.exercise)
            }
            vm.expand = !vm.expand
        },
        onClickDelete() {
            const vm = this
            vm.$emit('click:delete')
        },
        onClickDuplicate() {
            const vm = this
            let template = JSON.parse(JSON.stringify(vm.exercise))
            template.uid = randomString()
            vm.$emit('click:duplicate', template)
        },
        onClickChange() {
            const vm = this
            let template = JSON.parse(JSON.stringify(vm.exercise))
            template.uid = randomString()
            vm.$emit('click:change', template)
        },
    },
    computed: {
        isYoutubeVideo() {
            const vm = this
            return vm.exercise?.youtubeUrl ? true : false
        },
        exerciseImg() {
            const vm = this
            let img = vm.exercise.img || (vm.exercise?.video?.thumb) || null
            if (vm.isYoutubeVideo) img = vm.getYoutubeImgUrl(vm.exercise.youtubeUrl)
            return img
        },
        exerciseVideo() {
            const vm = this
            return vm.isYoutubeVideo ? vm.exercise?.youtubeUrl : vm.exercise?.video?.url || null
        },
        sets() {
            const vm = this
            return vm.exercise && vm.exercise.config && vm.exercise.config.sets || []
        },
        exerciseConfig() {
            const vm = this
            return vm.exercise && vm.exercise.config || null
        },
        isTabata() {
            const vm = this
            return vm.isCircuit && vm.circuitType === 'tabata'
        },
        noConfigYet() {
            const vm = this
            if (vm.isTabata) return false
            if (!vm.isCircuit && vm.sets.length === 0) return true
            return vm.isCircuit && !vm.exerciseConfig
        },
        categoryExercise() {
            const vm = this
            return vm.exercise && vm.exercise.categories && vm.exercise.categories[0] || null
        },
        exerciseSets() {
            const vm = this

            if (vm.isTabata) return ''

            if (vm.isCircuit) {
                let quantitySets = vm.block.sets + 'x'
                let value = vm.exercise.config?.value || 1
                let type = vm.$t(`exercises.short_${vm.exercise.config?.type || 'reps'}`)
                return `${vm.createBlockMode ? '' : quantitySets}${value}${type}`
            }

            let quantitySets = vm.sets.length
            if (quantitySets === 0) return vm.$t('common.not_available_short')
            let type = vm.$t(`exercises.${vm.sets[0].data[0].short_name}`)

            let result = ''

            if (quantitySets === 1) {
                result = `${quantitySets}x${vm.sets[0].data[0].value}${type}`
            } else {
                let values = []
                let current = null
                let sameValue = true
                vm.sets.forEach(set => {
                    if (!current) current = set.data[0].value
                    values.push(set.data[0].value)
                    if (current !== set.data[0].value) sameValue = false
                    current = set.data[0].value
                })

                if (sameValue) {
                    result = `${quantitySets}x${values[0]}${type}`
                } else {
                    result = `${quantitySets}x${values.join(`${type},`)}${type}`
                }
            }
            return result
        },
        isCircuit(vm = this) {
            return vm.block?.isCircuit || vm.exercise?.isCircuit || false
        },
    },
}
</script>

<style scoped>
.bex-card {
    border-radius: 10px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
    overflow: hidden;
    transition: border-color .15s ease;
}

.bex-card--open {
    border-color: var(--v-secondary-base);
}

.bex-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    cursor: pointer;
    transition: background .12s;
}

.bex-row:hover {
    background: var(--v-surface2-base);
}

.bex-drag {
    cursor: grab;
    flex-shrink: 0;
}

.bex-thumb {
    flex: 0 0 48px !important;
    max-width: 48px !important;
    min-width: 48px !important;
    height: 42px !important;
    padding: 0 !important;
    border-radius: 8px !important;
    flex-shrink: 0;
}

.bex-info {
    flex: 1;
    min-width: 0;
}

.bex-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.bex-sets {
    font-size: 11px;
    margin-top: 2px;
    font-weight: 600;
}

.theme--dark .bex-sets {
    color: var(--v-primary-base);
}

.theme--light .bex-sets {
    color: var(--v-accent-base);
}

.theme--dark .bex-sets--error,
.theme--light .bex-sets--error {
    color: var(--v-error-base);
}

.bex-menu-btn,
.bex-arrow {
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base);
}

.bex-menu-btn:hover,
.bex-arrow:hover {
    border-color: var(--v-txtMuted-base);
}

.bex-chevron {
    flex-shrink: 0;
    display: flex;
    transition: transform .2s ease;
}

.bex-chevron.open {
    transform: rotate(180deg);
}

.bex-body {
    padding: 0 10px 10px;
    border-top: 1px solid var(--v-outline-base);
}
</style>